import React from 'react';
import { Tooltip, Box, Typography, Stack } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { ITicketPrize } from '../../../../Core/State/ILobbyTournamentState';

interface TicketPrizesTooltipProps {
    ticketPrizes?: ITicketPrize[];
    position?: number; // For single position tooltips
    children: React.ReactElement;
}

const TicketPrizesTooltip: React.FC<TicketPrizesTooltipProps> = ({
    ticketPrizes,
    position,
    children
}) => {
    if (!ticketPrizes || ticketPrizes.length === 0) {
        return children;
    }

    // Single position tooltip (for rankings)
    if (position !== undefined) {
        const ticket = ticketPrizes.find(t => t.position === position);
        if (!ticket) return children;

        return (
            <Tooltip
                title={
                    <Box sx={{
                        p: 0.6,
                        backgroundColor: '#232329',
                        borderRadius: '3px',
                        border: '1px solid #3A3B40',
                        fontFamily: 'Inter, sans-serif'
                    }}>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                            <ConfirmationNumberIcon sx={{ fontSize: '13px', color: '#2e6f6b' }} />
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#f5f5f5', fontFamily: 'Inter, sans-serif' }}>
                                {ticket.ticketName}
                            </Typography>
                        </Stack>
                    </Box>
                }
                placement="top"
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'transparent',
                            '& .MuiTooltip-arrow': {
                                color: '#3A3B40',
                            },
                        },
                    },
                }}
            >
                {children}
            </Tooltip>
        );
    }

    // Multiple tickets tooltip (for prize pool display)
    const ticketsByName = ticketPrizes.reduce((acc, ticket) => {
        if (!acc[ticket.ticketName]) {
            acc[ticket.ticketName] = [];
        }
        acc[ticket.ticketName].push(ticket.position);
        return acc;
    }, {} as Record<string, number[]>);

    return (
        <Tooltip
            title={
                <Box sx={{
                    p: 0.8,
                    backgroundColor: '#232329',
                    borderRadius: '4px',
                    border: '1px solid #3A3B40',
                    fontFamily: 'Inter, sans-serif'
                }}>
                    <Stack spacing={0.6}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.2 }}>
                            <EmojiEventsIcon sx={{ fontSize: '13px', color: '#2e6f6b' }} />
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#2e6f6b', letterSpacing: '0.4px', fontFamily: 'Inter, sans-serif' }}>
                                TICKET PRIZES
                            </Typography>
                        </Stack>
                        {Object.entries(ticketsByName).map(([ticketName, positions]) => {
                            const sortedPositions = positions.sort((a, b) => a - b);
                            const positionText = sortedPositions.length === 1
                                ? `${sortedPositions[0]}`
                                : sortedPositions.join(', ');

                            return (
                                <Box
                                    key={ticketName}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.6,
                                        py: 0.3,
                                        px: 0.5,
                                        backgroundColor: 'rgba(46, 111, 107, 0.1)',
                                        borderRadius: '3px',
                                        borderLeft: '2px solid #2e6f6b'
                                    }}
                                >
                                    <ConfirmationNumberIcon sx={{ fontSize: '12px', color: '#2e6f6b', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#f5f5f5', lineHeight: 1.2, fontFamily: 'Inter, sans-serif' }}>
                                            {ticketName}
                                        </Typography>
                                        <Typography sx={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.2, fontFamily: 'Inter, sans-serif' }}>
                                            Position{sortedPositions.length > 1 ? 's' : ''}: {positionText}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            }
            placement="top"
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'transparent',
                        '& .MuiTooltip-arrow': {
                            color: '#3A3B40',
                        },
                    },
                },
            }}
        >
            {children}
        </Tooltip>
    );
};

export default TicketPrizesTooltip;
